import * as RadixToast from '@radix-ui/react-toast';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { cn } from './cn';

/**
 * Короткі повідомлення про результат дії.
 *
 * До появи цього кожна сторінка вбудовувала власний рядок «Saved» чи
 * «Failed» у своє тіло: те саме писалось десятки разів по-різному, а на
 * довгих сторінках повідомлення зʼявлялось поза екраном.
 *
 * Черга навмисно поза React: `toast()` викликають із мутацій і обробників,
 * де хука немає. Провайдер лише підписаний на неї.
 */
export interface ToastMessage {
	id: number;
	title: string;
	description?: string;
	tone: 'success' | 'error';
}

type Listener = (messages: ToastMessage[]) => void;

let queue: ToastMessage[] = [];
let listeners: Listener[] = [];
let nextId = 1;

function emit(): void {
	for (const listener of listeners) listener(queue);
}

export function toast(message: Omit<ToastMessage, 'id'>): void {
	queue = [...queue, { ...message, id: nextId++ }];
	emit();
}

export const toastSuccess = (title: string, description?: string): void =>
	toast({ title, description, tone: 'success' });
export const toastError = (title: string, description?: string): void =>
	toast({ title, description, tone: 'error' });

function dismiss(id: number): void {
	queue = queue.filter((m) => m.id !== id);
	emit();
}

export function ToastViewport(): ReactNode {
	const [messages, setMessages] = useState<ToastMessage[]>(queue);

	useEffect(() => {
		listeners = [...listeners, setMessages];
		return (): void => {
			listeners = listeners.filter((l) => l !== setMessages);
		};
	}, []);

	return (
		<RadixToast.Provider duration={5000} swipeDirection="right">
			{messages.map((message) => (
				<RadixToast.Root
					key={message.id}
					onOpenChange={(open) => !open && dismiss(message.id)}
					className={cn(
						'flex items-start gap-2.5 rounded-card border bg-surface p-3 shadow-e2',
						'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-4',
						message.tone === 'success' ? 'border-success/30' : 'border-danger/30',
					)}
				>
					{message.tone === 'success' ? (
						<CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
					) : (
						<TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger" />
					)}
					<div className="min-w-0 flex-1">
						<RadixToast.Title className="text-sm font-medium">{message.title}</RadixToast.Title>
						{message.description && (
							<RadixToast.Description className="mt-0.5 text-xs leading-relaxed text-fg-muted">
								{message.description}
							</RadixToast.Description>
						)}
					</div>
					<RadixToast.Close
						aria-label="Dismiss"
						className="shrink-0 text-fg-subtle transition-colors hover:text-fg"
					>
						<X className="size-3.5" />
					</RadixToast.Close>
				</RadixToast.Root>
			))}
			<RadixToast.Viewport className="fixed bottom-4 right-4 z-[60] flex w-[min(22rem,92vw)] flex-col gap-2 outline-none" />
		</RadixToast.Provider>
	);
}
